import { UntypedFormControl } from '@angular/forms';

const PRICE_REGEX = /^\d{0,8}(\.\d{1,2})?$/;
export const URL_REGEX = /^((https?:\/\/)?([\w-]+(\.[\w-]+)+|localhost)\.?(:\d+)?((\/)?\S*)?)$/i;
export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const SUBDOMAIN_REGEX = /(^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$)/;
// Following regex matches North American, South African, UK, and Australian phone numbers, with some support for extensions
const PHONE_NUMBER_REGEX =
  /(^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$)|(^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$)|(^(\+\d{2}[ -]{0,1}){0,1}(((\({0,1}[ -]{0,1})0{0,1}\){0,1}[2|3|7|8]{1}\){0,1}[ -]*(\d{4}[ -]{0,1}\d{4}))|(1[ -]{0,1}(300|800|900|902)[ -]{0,1}((\d{6})|(\d{3}[ -]{0,1}\d{3})))|(13[ -]{0,1}([\d -]{5})|((\({0,1}[ -]{0,1})0{0,1}\){0,1}4{1}[\d -]{8,10})))$)|((\(0\d\d\)\s\d{3}[\s-]+\d{4})|(0\d\d[\s-]+\d{3}[\s-]+\d{4})|(0\d{9})|(\+\d\d\s?[(\s]\d\d[)\s]\s?\d{3}[\s-]?\d{4}))/;
const MARKET_ID_REGEX = /(^[a-z0-9-]*$)/;

/**
 * import from https://github.com/ReactiveX/rxjs/blob/5.5.8/src/util/isNumeric.ts
 */
const isArray = Array.isArray || (<T>(x: any): x is T[] => x && typeof x.length === 'number');

function isNumeric(val: any): val is number {
  return !isArray(val) && val - parseFloat(val) + 1 >= 0;
}

// TODO: put in a shared lib like @vendasta/core
export class Validator {
  static isPrice(control: UntypedFormControl): any {
    if (control.value && typeof control.value === 'string' && control.value.match(PRICE_REGEX)) {
      return null;
    } else if (
      control.value &&
      typeof control.value === 'number' &&
      parseFloat((control.value * 100).toFixed(2)) % 1 === 0
    ) {
      // modulus with floating point bases is broken
      return null;
    } else if (!control.value) {
      return null;
    } else {
      return { invalidPrice: true };
    }
  }

  static isUrl(control: UntypedFormControl): any {
    if (control.value && control.value.match(URL_REGEX)) {
      return null;
    } else if (control.value) {
      return { invalidUrl: true };
    }
  }

  static isEmailList(control: UntypedFormControl): any {
    if (control.value) {
      const emails = control.value
        .toString()
        .split(/[,;]+/)
        .map((email) => email.trim());
      const invalidEmails = emails.filter((email) => !email.match(EMAIL_REGEX));
      if (invalidEmails.length) {
        return { invalidEmail: true };
      }
    }
  }

  static isEmail(control: UntypedFormControl): any {
    if (control.value && control.value.match(EMAIL_REGEX)) {
      return null;
    } else if (control.value) {
      return { invalidEmail: true };
    }
  }

  static isPhoneNumber(control: UntypedFormControl): any {
    if (control.value && control.value.match(PHONE_NUMBER_REGEX)) {
      return null;
    } else if (control.value) {
      return { invalidPhoneNumber: true };
    }
  }

  static isInRange = (lower: number, upper: number) => {
    return (control: UntypedFormControl) => {
      const value = control.value;
      if (!isNumeric(value) || (lower !== null && Number(value) < lower) || (upper !== null && Number(value) > upper)) {
        return { outOfRange: true };
      }
      return null;
    };
  };

  static isValidMarketId(control: UntypedFormControl): any {
    if (control.value && control.value.match(MARKET_ID_REGEX)) {
      return null;
    } else if (control.value) {
      return { invalidMarketId: true };
    }
  }

  static isMinimumLength = (minimumLength: number) => {
    return (control: UntypedFormControl): any => {
      if (control.value && control.value.length >= minimumLength) {
        return null;
      } else if (control.value) {
        return { minlength: true };
      }
    };
  };

  static isValidSubdomain(control: UntypedFormControl): any {
    if (control.value && control.value.match(SUBDOMAIN_REGEX)) {
      return null;
    } else if (control.value) {
      return { invalidSubdomain: true };
    }
  }

  static isGreaterThanZero = () => {
    return (control: UntypedFormControl) => {
      if (control.value <= 0) {
        return { isLessThanOrEqualToZero: true };
      }
      return null;
    };
  };

  static errorMessage(validatorName: string, validatorValue?: any): string {
    const config = {
      required: 'Required',
      minlength: `Minimum length ${validatorValue.requiredLength}`,
      invalidPrice: `Invalid price`,
      outOfRange: `Out of range`,
      invalidEmail: 'Invalid Email',
      invalidPhoneNumber: 'Invalid Phone Number',
      invalidZipCode: 'Invalid Zip/Postal Code',
      invalidSubdomain: 'Invalid Subdomain',
    };

    return config[validatorName];
  }
}
